if (!customElements.get('collection-filters')) {
  customElements.define(
    'collection-filters',
    class CollectionFilters extends HTMLElement {
      connectedCallback() {
        // Desktop: the filters sit in a left column and work as accordions (closed by default).
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.expanded = new Set();
        this.desktop.addEventListener('change', () => {
          this.closeAll();
          this.syncAccordions();
        });
        this.syncAccordions();

        this.addEventListener('click', this.onClick.bind(this));
        document.addEventListener('click', (event) => {
          if (!event.target.closest || !event.target.closest('[data-filter]')) this.closeAll();
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeAll();
        });
        window.addEventListener('popstate', () => this.load(window.location.href, { push: false }));
      }

      onClick(event) {
        const toggle = event.target.closest('[data-filter-toggle]');
        if (toggle) {
          const dropdown = toggle.closest('[data-filter]');
          if (this.isAccordion(dropdown)) {
            const key = dropdown.dataset.filter;
            const expand = !this.expanded.has(key);
            if (expand) {
              this.expanded.add(key);
            } else {
              this.expanded.delete(key);
            }
            this.animateAccordion(dropdown, expand);
            return;
          }
          const wasOpen = dropdown.classList.contains('is-open');
          this.closeAll();
          if (!wasOpen) this.open(dropdown);
          return;
        }

        const link = event.target.closest('a[data-filter-link]');
        if (link) {
          event.preventDefault();
          this.load(link.href, { push: true, keepOpen: !link.hasAttribute('data-filter-close') });
        }
      }

      isAccordion(dropdown) {
        return this.desktop.matches && Boolean(dropdown.closest('.collection-custom__sidebar'));
      }

      syncAccordions() {
        this.querySelectorAll('.collection-custom__sidebar [data-filter]').forEach((dropdown) => {
          const expanded = this.expanded.has(dropdown.dataset.filter);
          dropdown.classList.toggle('is-expanded', expanded);
          if (this.desktop.matches) {
            dropdown.querySelector('[data-filter-toggle]').setAttribute('aria-expanded', String(expanded));
          } else if (!dropdown.classList.contains('is-open')) {
            dropdown.querySelector('[data-filter-toggle]').setAttribute('aria-expanded', 'false');
          }
        });
      }

      // Smooth open/close: from the current height to the content height (or 0), then back to "auto".
      animateAccordion(dropdown, expand) {
        const panel = dropdown.querySelector('.collection-custom__panel');
        const toggle = dropdown.querySelector('[data-filter-toggle]');
        toggle.setAttribute('aria-expanded', String(expand));
        if (!panel || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          dropdown.classList.toggle('is-expanded', expand);
          return;
        }

        panel.style.height = `${panel.getBoundingClientRect().height}px`;
        void panel.offsetHeight; // apply the starting height before changing it
        dropdown.classList.toggle('is-expanded', expand);
        panel.style.height = expand ? `${panel.scrollHeight}px` : '0px';

        const done = (event) => {
          if (event.target !== panel || event.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', done);
          panel.style.height = '';
        };
        panel.addEventListener('transitionend', done);
      }

      open(dropdown) {
        this.slide(dropdown, () => dropdown.classList.add('is-open'));
        dropdown.querySelector('[data-filter-toggle]').setAttribute('aria-expanded', 'true');
      }

      // "Sort by" in the left column opens in the page flow: its height goes smoothly from the current height to the
      // new one (like the filter accordions), then back to "auto".
      slide(dropdown, change) {
        const panel = dropdown.querySelector('.collection-custom__panel');
        if (!panel || !panel.matches('.collection-custom__side .collection-custom__panel--sort') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          change();
          return;
        }
        panel.style.height = `${panel.getBoundingClientRect().height}px`;
        void panel.offsetHeight;
        change();
        panel.style.height = dropdown.classList.contains('is-open') ? `${panel.scrollHeight}px` : '0px';
        const done = (event) => {
          if (event.target !== panel || event.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', done);
          panel.style.height = '';
        };
        panel.addEventListener('transitionend', done);
      }

      closeAll() {
        this.querySelectorAll('[data-filter].is-open').forEach((dropdown) => {
          if (this.isAccordion(dropdown)) {
            dropdown.classList.remove('is-open');
            return;
          }
          this.slide(dropdown, () => dropdown.classList.remove('is-open'));
          dropdown.querySelector('[data-filter-toggle]').setAttribute('aria-expanded', 'false');
        });
      }

      // Re-render the section with the new filters/sort without a full page reload.
      async load(url, { push, keepOpen = false }) {
        const openDropdown = this.querySelector('[data-filter].is-open');
        const openKey = keepOpen && openDropdown ? openDropdown.dataset.filter : null;

        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('section_id', this.dataset.sectionId);
        this.classList.add('is-loading');

        try {
          const response = await fetch(requestUrl);
          const html = await response.text();
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('collection-filters');
          if (!fresh) throw new Error('Missing section');

          this.innerHTML = fresh.innerHTML;
          this.syncAccordions();
          if (openKey) {
            const dropdown = Array.from(this.querySelectorAll('[data-filter]')).find(
              (element) => element.dataset.filter === openKey
            );
            if (dropdown) {
              // Re-open instantly: the panel was already open before the refresh.
              dropdown.classList.add('is-instant');
              this.open(dropdown);
              requestAnimationFrame(() => requestAnimationFrame(() => dropdown.classList.remove('is-instant')));
            }
          }
          if (push) window.history.pushState({}, '', url);
        } catch (error) {
          window.location.href = url;
        } finally {
          this.classList.remove('is-loading');
        }
      }
    }
  );
}
